// VendorManagement.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import Layout from "../Component/Layout";
import axios from "../http"; // axios instance that attaches the JWT (needed once the API enforces RBAC)
import PropTypes from "prop-types"; // Added to fix ESLint errors
import { MASTER_API } from "../apiConfig";




/* ======================================================================
   RBAC  (module id must match TILE_REGISTRY / FIELD_REGISTRY key)
   ----------------------------------------------------------------------
   Access types (from Create Role):
     CREATE  -> can add partners (CREATE field list = fields shown on the form)
     DISPLAY -> can view partners (DISPLAY field list = which values are masked)
     CHANGE  -> can view + edit/delete (CHANGE field list = which fields are editable)
   Admin / Full Access users bypass every check.
   Fails closed: until permissions are loaded nobody gets access.
   NOTE: this only controls the UI. The same checks must also be enforced
   in the backend routes — see the notes delivered with this file.
   ====================================================================== */
const MASK = "••••••";

// Same component used on the other modules. If it lives in its own file in your
// project, delete this block and use:  import MaskedValue from "../Component/MaskedValue";
// (adjust the path)
const MaskedValue = ({ height = 34 }) => (
  <div
    style={{
      height,
      display: "flex",
      alignItems: "center",
      padding: "0 10px",
      background: "#f1f5f9",
      border: "1px solid #e2e8f0",
      borderRadius: 4,
      color: "transparent",
      textShadow: "0 0 8px rgba(51,65,85,0.65)",
      userSelect: "none",
      fontSize: 13,
      letterSpacing: 2
    }}
  >
    ████████
  </div>
);


const AccessContext = React.createContext(null);

const useBusinessPartnerAccess = () => {
  const [state, setState] = useState({
    loading: true,
    isAdmin: false,
    perm: { can_view: 0, can_create: 0, can_edit: 0 },
    fields: { DISPLAY: null, CHANGE: null, CREATE: null } // type -> { field_id: bool } | null (= not configured)
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // The backend works out this user's access (all roles combined) and
        // field rules, so nothing depends on what the login stored in the browser.
        const res = await axios.get(`${MASTER_API}/business-partner/my-access`);
        const a = res.data || {};
        const types = new Set((a.types || []).map((t) => String(t).toUpperCase()));

        // type -> { field_id: false } for every field turned OFF, or null if none
        const toMap = (list) =>
          Array.isArray(list) && list.length
            ? Object.fromEntries(list.map((f) => [f, false]))
            : null;

        const next = {
          loading: false,
          isAdmin: !!a.admin,
          perm: {
            can_view: ["DISPLAY", "CHANGE", "REVIEW", "CONVERT"].some((t) => types.has(t)) ? 1 : 0,
            can_create: types.has("CREATE") ? 1 : 0,
            can_edit: types.has("CHANGE") ? 1 : 0
          },
          fields: {
            DISPLAY: toMap(a.off?.DISPLAY),
            CHANGE: toMap(a.off?.CHANGE),
            CREATE: toMap(a.off?.CREATE)
          }
        };
        if (!cancelled) setState(next);
      } catch (e) {
        console.error("[BusinessPartner RBAC] could not load /business-partner/my-access", e);
        // fail closed
        if (!cancelled) setState((st) => ({ ...st, loading: false, isAdmin: false }));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const { loading, isAdmin, perm, fields } = state;

  // state of one field for the current modal mode
  //  create       -> hidden when the CREATE list turns it off
  //  view         -> masked when the DISPLAY list turns it off
  //  edit         -> masked if DISPLAY hides it, read-only if CHANGE turns it off
  //  *_draft      -> drafts are handled by users who can create; no field rules
  const fieldState = (field, mode) => {
    if (isAdmin) return { hidden: false, masked: false, readOnly: false };

    const off = (type) => fields[type] && fields[type][field] === false;

    if (mode === "create") return { hidden: !!off("CREATE"), masked: false, readOnly: false };
    if (mode === "view") return { hidden: false, masked: !!off("DISPLAY"), readOnly: false };
    if (mode === "edit") {
      return { hidden: false, masked: !!off("DISPLAY"), readOnly: !!off("CHANGE") };
    }
    return { hidden: false, masked: false, readOnly: false };
  };

  return {
    loading,
    isAdmin,
    canView: isAdmin || !!perm.can_view,
    canCreate: isAdmin || !!perm.can_create,
    canEdit: isAdmin || !!perm.can_edit,
    fieldState
  };
};


// Wraps a whole form block (label + control) that is not a plain FormInput:
// dropdowns, react-select and the address blocks.
//  hidden   -> not rendered (Create form with the field turned off)
//  masked   -> value replaced by dots (Display role with the field unchecked)
//  readOnly -> block is shown but can't be changed (Change role with the field unchecked)
const Guard = ({ field, label, col = "col-md-4", children }) => {
  const ctx = useContext(AccessContext);
  const st = ctx ? ctx.fieldState(field) : { hidden: false, masked: false, readOnly: false };

  if (st.hidden) return null;

  if (st.masked) {
    return (
      <div className={col}>
        <label style={styles.compactLabel}>{label}</label>
        <MaskedValue />
      </div>
    );
  }

  if (st.readOnly && React.isValidElement(children)) {
    return React.cloneElement(children, {
      style: { ...(children.props.style || {}), pointerEvents: "none", opacity: 0.7 }
    });
  }

  return children;
};

const FormInput = ({
  label,
  field,
  type = "text",
  options = null,
  formData = {},
  handleInputChange = () => { },
  handleFileChange = () => { },
  isReadOnly = false
}) => {
  const ctx = useContext(AccessContext);
  const st = ctx ? ctx.fieldState(field) : { hidden: false, masked: false, readOnly: false };

  if (st.hidden) return null;

  const locked = isReadOnly || st.readOnly || st.masked;

  return (
    <div className="col-md-4">

      <label style={styles.compactLabel}>
        {label}
      </label>

      {st.masked ? (

        <MaskedValue />

      ) : options ? (

        <select
          className="form-select"
          style={styles.compactInput}
          value={formData[field] || ""}
          onChange={(e) =>
            handleInputChange(field, e.target.value)
          }
          disabled={locked}
        >
          <option value="">
            Select {label}
          </option>

          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}

        </select>

      ) : (

        <input
          type={type}
          className="form-control"
          style={styles.compactInput}
          value={formData[field] || ""}
          onChange={(e) =>
            handleInputChange(field, e.target.value)
          }
          disabled={locked}
        />

      )}

    </div>
  );
};

const safeParseArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  if (typeof value === "string") {
    // Try proper JSON array first (new format, e.g. email)
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // not JSON - fall through to legacy plain/comma-separated handling
    }

    // Legacy format: plain string or comma-separated values
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }

  return [];
};


const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "28px",
    height: "28px",
    borderRadius: "4px",
    fontSize: "12px",
    borderColor: "#cbd5e1",
    boxShadow: "none"
  }),
  valueContainer: (base) => ({
    ...base,
    height: "28px",
    padding: "0 12px",
    display: "flex",
    alignItems: "center"
  }),

  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0
  }),

  dropdownIndicator: (base) => ({
    ...base,
    padding: 4
  }),

  menu: (base) => ({
    ...base,
    fontSize: "13px",
    zIndex: 9999
  }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
};


const VendorManagement = () => {
  /* ---------------------- RBAC ---------------------- */
  const access = useBusinessPartnerAccess();

  /* ---------------------- Local UI Helpers ---------------------- */
  const Toast = ({ toasts, remove }) => (
    <div style={styles.toastContainer}>
      {toasts.map(t => (
        <div key={t.id} style={{ ...styles.toast, ...(t.type === "error" ? styles.toastError : {}) }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.title}</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>{t.message}</div>
          <button onClick={() => remove(t.id)} style={styles.toastClose}>×</button>
        </div>
      ))}
    </div>
  );

  // ESLint Fix: Added prop types for Toast
  Toast.propTypes = {
    toasts: PropTypes.array.isRequired,
    remove: PropTypes.func.isRequired,
  };

  const useToasts = () => {
    const [toasts, setToasts] = useState([]);
    const add = (title, message = "", type = "info") => {
      const id = Math.random().toString(36).slice(2);
      setToasts(prev => [...prev, { id, title, message, type }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    };
    const remove = (id) => setToasts(prev => prev.filter(t => t.id !== id));
    return { toasts, add, remove };
  };

  const { toasts, add: addToast, remove: removeToast } = useToasts();

  /* ---------------------- Search & Filters ---------------------- */
  const [find, setFind] = useState("");
  const [type, setType] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  //eslint-disable-next-line
  const [by, setBy] = useState("");
  const [segment, setSegment] = useState("");
  const [firstName, setFirstName] = useState("");
  // const [lastName, setLastName] = useState("");
  //eslint-disable-next-line
  const [panValue, setPanValue] = useState("");

  /* ---------------------- Table & Pagination ---------------------- */
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [termsDraft, setTermsDraft] = useState("");
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [vendorDocFiles, setVendorDocFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const limit = 30;

  const [deletedConflictCodes, setDeletedConflictCodes] = useState([]);
const [activeConflictCodes, setActiveConflictCodes] = useState([]);
const [showDraftReplaceConfirm, setShowDraftReplaceConfirm] = useState(false);
const [draftReplaceTarget, setDraftReplaceTarget] = useState(null);
const [draftReplaceType, setDraftReplaceType] = useState("deleted");
const [showBulkReplaceConfirm, setShowBulkReplaceConfirm] = useState(false);
  
  const filteredVendors = vendors.filter((v) => {
  const notDeleted = (v.status || "Active") !== "Deleted";

  const matchName = firstName
    ? v.business_partner_name?.toLowerCase().includes(firstName.toLowerCase())
    : true;

  const matchType = type
    ? v.partner_type?.toLowerCase() === type.toLowerCase()
    : true;

  const matchBizType = by
    ? v.business_type?.toLowerCase() === by.toLowerCase()
    : true;

  const matchSegment = segment
    ? v.segment?.toLowerCase() === segment.toLowerCase()
    : true;

  return (
    notDeleted &&
    matchName &&
    matchType &&
    matchBizType &&
    matchSegment
  );
});

const paginatedData = filteredVendors.slice((page - 1) * limit, page * limit);
const totalPages = Math.max(1, Math.ceil(filteredVendors.length / limit));
  const searchTimerRef = useRef(null);

const MAX_DOC_SIZE_BYTES = 50 * 1024; // 50 KB

const handleDocumentUpload = (e) => {
  const rawFiles = Array.from(e.target.files);
  e.target.value = ""; // allow re-selecting the same file name later

  const okFiles = [];
  const rejected = [];
  rawFiles.forEach((f) => {
    if (f.size > MAX_DOC_SIZE_BYTES) rejected.push(f.name);
    else okFiles.push(f);
  });

  if (rejected.length) {
    addToast(
      "Some files skipped",
      `${rejected.length} file(s) exceed the 50 KB limit: ${rejected.join(", ")}`,
      "error"
    );
  }

  if (okFiles.length) {
    setDocuments((prev) => [...prev, ...okFiles]);
  }
};

const removeSelectedDocument = (idx) => {
  setDocuments((prev) => prev.filter((_, i) => i !== idx));
};

  const [showTermsModal, setShowTermsModal] = useState(false);
  // ==========================================
  // 🌟 UNIFIED MODAL & FORM STATE
  // ==========================================
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "CREATE" | "VIEW" | "EDIT" | "DRAFT"
  const [activeTab, setActiveTab] = useState("basic");

  const initialFormState = {
    id: "", partner_code: "", partner_type: "Supplier", business_partner_name: "", legal_name: "",
    business_type: "", category_master: "", business_partner_group: "",
    contact_details: "",  pan: "", pan_status: "", date_of_corporation: "",
    pan_holder_category: "", gstin: "", cin: "", udyam_registration: "",
    shop_establishment_license: "", bank_name: "", branch: "", account_number: "",
    ifsc_code: "", tds_tan: "", esi_pf: "", professional_tax: "", import_export_code: "",
    pan_gstin_or_bank_validation: "",
    shipping_line1: "",
    shipping_line2: "",
    shipping_city: "",
    shipping_zip: "",
    shipping_state: "",
    shipping_country: "India",

    billing_line1: "",
    billing_line2: "",
    billing_city: "",
    billing_zip: "",
    billing_state: "",
    billing_country: "India",

    // Single "Address" — used only for Supplier partners
    address_line1: "",
    address_line2: "",
    address_city: "",
    address_zip: "",
    address_state: "",
    address_country: "India",
    terms_and_conditions: "",
    sales_person: "",
    business_partner_responsible: "",
    partner_responsible_phone: "",
    partner_responsible_email: "",
    // Files

  };

  const [formData, setFormData] = useState(initialFormState);
  const [partnerLogs, setPartnerLogs] = useState([]);
  const [showPartnerModal, setShowPartnerModal] = useState(false);

const [partnerContacts, setPartnerContacts] =
  useState([]);
  // ==========================================
  // 🌟 DRAFT & UPLOAD STATES
  // ==========================================
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftsList, setDraftsList] = useState([]);
  const [isPublishingAll, setIsPublishingAll] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [salesPersons, setSalesPersons] =
    useState([]);
    const [segments, setSegments] = useState([]);
    const [businessPartnerResponsibles, setBusinessPartnerResponsibles] =
      useState([]);

  /* ---------------------- API Calls ---------------------- */
  const fetchVendors = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${MASTER_API}/business-partner/all`);
      if (res.data.success) {
        setVendors(res.data.data);
        setTotal(res.data.data?.length || 0);
      }
    } catch (err) { addToast("Failed to load vendors", String(err), "error"); }
    finally { setLoading(false); }
  };

  const fetchSalesPersons = async () => {

    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/dynamic/sales-person`
      );

      setSalesPersons(res.data || []);

    } catch (err) {

      console.log(err);

    }

  };

  const fetchBusinessPartnerResponsibles = async () => {

    try {

      const res = await axios.get(
        `${MASTER_API}/configuration/dynamic/business-partner-responsible`
      );

      setBusinessPartnerResponsibles(res.data || []);

    } catch (err) {

      console.log(err);

    }

  };
  useEffect(() => {
  if (access.loading) return;

  if (access.canView) fetchVendors();
  if (access.canCreate) loadDrafts();
  fetchSalesPersons();
  fetchBusinessPartnerResponsibles();
  fetchStates();
  fetchCities();
  fetchCountries();
  fetchSegments();

}, [access.loading]); // eslint-disable-line

  const handleSearch = async () => {
    if (access.loading || !access.canView) return;
    setLoading(true);
    try {
      const res = await axios.post(`${MASTER_API}/business-partner/search`, { find, type, by, business_partner_name: firstName, panValue, page, limit });
      if (res.data.success) {
        setVendors(res.data.data);
        setTotal(res.data.total || res.data.data?.length || 0);
      }
    } catch (err) { addToast("Search failed", String(err), "error"); }
    finally { setLoading(false); }
  };

  const triggerSearch = () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(handleSearch, 450);
  };

  useEffect(() => { triggerSearch(); }, [find, type, by, firstName, panValue, page, access.loading, access.canView]); // eslint-disable-line
useEffect(() => { setPage(1); }, [type, by, firstName]); // eslint-disable-line

  // --- Handlers ---
  const handleInputChange = (field, value) => {
    // Business Partner Name should always be stored/shown in uppercase
    const finalValue =
      field === "business_partner_name" && typeof value === "string"
        ? value.toUpperCase()
        : value;
    setFormData(prev => ({ ...prev, [field]: finalValue }));
  };

  const handleFileChange = (field, file) => {
    setFormData(prev => ({ ...prev, [field]: file }));
  };
  const addContact = () => {
  setPartnerContacts([
    ...partnerContacts,
    {
      phone_number: "",
      contact_person_name: "",
      contact_person_designation: "",
      email: ""
    }
  ]);
};

const updateContact = (
  index,
  field,
  value
) => {
  const updated = [...partnerContacts];

  updated[index][field] = value;

  setPartnerContacts(updated);
};

const removeContact = (index) => {
  setPartnerContacts(
    partnerContacts.filter(
      (_, i) => i !== index
    )
  );
};

  const fetchStates = async () => {
    try {
      const res = await axios.get(
        `${MASTER_API}/configuration/state`
      );

      setStates(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get(
        `${MASTER_API}/configuration/city`
      );

      setCities(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(
        `${MASTER_API}/configuration/country`
      );

      setCountries(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };
  const fetchSegments = async () => {
  try {
    const res = await axios.get(
      `${MASTER_API}/configuration/dynamic/customer-segment`
    );

    setSegments(res.data || []);
  } catch (err) {
    console.log(err);
  }
};

  const openCreateModal = () => {
    if (!access.canCreate) return;
    setFormData(initialFormState);
    setPartnerContacts([]);
    setVendorDocFiles([]); 
    setModalMode("create");
    setActiveTab("basic");
    setShowModal(true);
  };

  const handleViewVendor = async (v) => {
    if (!access.canView) return;
    try {
      const res = await axios.get(`${MASTER_API}/business-partner/${v.id}`);

      const logRes = await axios.get(
        `${MASTER_API}/business-partner/change-logs/${v.id}`
      );

      setPartnerLogs(logRes.data || []);
      if (res.data.success) {
        const data = res.data.data;

        // Format dates correctly for input type="date"
        if (data.date_of_corporation) data.date_of_corporation = data.date_of_corporation.split('T')[0];
        if (data.pan_date_of_issue) data.pan_date_of_issue = data.pan_date_of_issue.split('T')[0];

        // 🌟 FIX: Normalize case-sensitivity for dropdowns (e.g., "customer" -> "Customer")
        if (data.partner_type) {
          data.partner_type = data.partner_type.charAt(0).toUpperCase() + data.partner_type.slice(1).toLowerCase();
        }



        setFormData({ ...initialFormState, ...data });

        if (data.documents && data.documents.length > 0) {
          setVendorDocFiles(
            data.documents.map((f) => ({
              name: f.file_name,
              filename: f.file_name,
              file_url: f.file_url,
              size: f.size || null,
              type: f.type || null,
              uploadedAt: f.uploadedAt || null,
            }))
          );
        } else {
          setVendorDocFiles([]);
        }

        setModalMode("view");
        setActiveTab("basic");
        setShowModal(true);
      }
    } catch (err) { addToast("Load failed", String(err), "error"); }
  };

  const saveOrUpdateVendor = async () => {
    if (modalMode === "create" && !access.canCreate) return;
    if (modalMode === "edit" && !access.canEdit) return;
    if (modalMode === "edit_draft" && !access.canCreate) return;
    const isUpdate =
      modalMode === "edit" ||
      modalMode === "edit_draft";
    const url =
      modalMode === "edit_draft"
        ? `${MASTER_API}/business-partner/update-draft/${formData.partner_code}`
        : isUpdate
          ? `${MASTER_API}/business-partner/update/${formData.id}`
          : `${MASTER_API}/business-partner/add`;
    const method = isUpdate ? "PUT" : "POST";


    const payload = new FormData();
    Object.keys(formData).forEach((key) => {

      if (
        formData[key] !== null &&
        formData[key] !== undefined &&
        formData[key] !== ""
      ) {

        // MULTIPLE EMAIL FIX
        if (key === "email") {

          payload.append(
            "email",
            JSON.stringify(formData.email || [])
          );

        }

        // FILES
        else if (formData[key] instanceof File) {

          payload.append(key, formData[key]);

        }

        // NORMAL FIELDS
        else {

          payload.append(key, formData[key]);

        }
      }
    });

    try {
      const res = await axios({ method, url, data: payload, headers: { "Content-Type": "multipart/form-data" } });

      if (res.data.success) {
        addToast(isUpdate ? "Business Partner Updated" : "Business Partner Created", "Saved successfully", "success");
        setShowModal(false);
        fetchVendors();
      } else {
        addToast("Error", res.data.message, "error");
      }
    } catch (err) {
      addToast("Failed to save", String(err), "error");
    }
  };

  const handleDeleteVendor = async (id) => {
    if (!access.canEdit) return;
    if (!window.confirm("Delete this partner?")) return;
    try {
      await axios.delete(`${MASTER_API}/business-partner/delete/${id}`);
      addToast("Deleted", "The partner has been removed", "success");
      fetchVendors();
    } catch (err) { addToast("Delete failed", String(err), "error"); }
  };

  // ==========================================
  // 🌟 DRAFT & UPLOAD LOGIC
  // ==========================================
  const loadDrafts = async () => {
  try {
    const res = await axios.get(`${MASTER_API}/business-partner/get-drafts`);
    if (res.data.success) {
      setDraftsList(res.data.drafts || []);
      setDeletedConflictCodes(res.data.deletedConflicts || []);
      setActiveConflictCodes(res.data.activeConflicts || []);
    }
  } catch (err) { console.error("Failed to load drafts", err); }
};

  const handleBulkExcelUpload = async (e) => {
  if (!access.canCreate) return;
  const file = e.target.files[0];
  if (!file) return;
  const uploadData = new FormData();
  uploadData.append("file", file);

  try {
    const res = await axios.post(`${MASTER_API}/business-partner/parse-excel`, uploadData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    if (res.data.success) {
      addToast("Success", res.data.message, "success");

      if (res.data.deletedConflicts?.length > 0) {
        addToast(
          "Notice",
          `${res.data.deletedConflicts.length} partner(s) match previously deleted entries. You'll be asked to confirm when publishing.`,
          "info"
        );
      }
      if (res.data.activeConflicts?.length > 0) {
        addToast(
          "Notice",
          `${res.data.activeConflicts.length} partner(s) already exist and will be overwritten. You'll be asked to confirm when publishing.`,
          "info"
        );
      }

      loadDrafts();
      setShowUploadModal(false);
      setShowDraftModal(true);
    } else {
      addToast("Notice", res.data.message, "info");
    }
  } catch (err) { addToast("Upload Failed", err.response?.data?.error || "Failed to upload file", "error"); }
  finally { e.target.value = null; }
};

  const handlePublishDraft = async (partner_code, replaceExisting = false) => {
  setPublishingId(partner_code);
  try {
    const res = await axios.post(`${MASTER_API}/business-partner/publish-draft`, { partner_code, replaceExisting });
    if (res.data.success) {
      addToast("Published", res.data.message, "success");
      setDeletedConflictCodes(prev => prev.filter(c => c !== partner_code));
      setActiveConflictCodes(prev => prev.filter(c => c !== partner_code));
      await loadDrafts();
      fetchVendors();
      return;
    }
    // needsConfirmation === true is handled by onClickPublishDraft before we get here in normal flow
  } catch (err) { addToast("Publish Failed", "Failed to publish draft", "error"); }
  finally { setPublishingId(null); }
};

const onClickPublishDraft = (partner_code) => {
  if (deletedConflictCodes.includes(partner_code)) {
    setDraftReplaceType("deleted");
    setDraftReplaceTarget(partner_code);
    setShowDraftReplaceConfirm(true);
  } else if (activeConflictCodes.includes(partner_code)) {
    setDraftReplaceType("active");
    setDraftReplaceTarget(partner_code);
    setShowDraftReplaceConfirm(true);
  } else {
    handlePublishDraft(partner_code, false);
  }
};

  const handleDeleteDraft = async (partner_code) => {
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;

    setDeletingId(partner_code);
    try {
      const res = await axios.delete(`${MASTER_API}/business-partner/delete-draft/${partner_code}`);
      if (res.data.success) {
        addToast("Deleted", res.data.message, "success");
        loadDrafts();
      } else {
        addToast("Notice", res.data.message || "Draft not found", "info");
      }
    } catch (err) {
      addToast("Delete Failed", err.response?.data?.error || "Failed to delete draft", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const publishAllDraftsApi = async (replaceExisting) => {
  setIsPublishingAll(true);
  try {
    const res = await axios.post(`${MASTER_API}/business-partner/publish-all-drafts`, { replaceExisting });
    if (res.data.success) {
      addToast("All Published", res.data.message, "success");
      setDeletedConflictCodes([]);
      setActiveConflictCodes([]);
      loadDrafts();
      fetchVendors();
      setShowDraftModal(false);
    }
  } catch (error) { addToast("Publish Error", error.response?.data?.message || "Error saving drafts", "error"); }
  finally { setIsPublishingAll(false); }
};

const handlePublishAllDrafts = () => {
  if (draftsList.length === 0) return;

  const conflictsInList = draftsList
    .map(d => d.partner_code)
    .filter(code => deletedConflictCodes.includes(code) || activeConflictCodes.includes(code));

  if (conflictsInList.length > 0) {
    setShowBulkReplaceConfirm(true);
    return;
  }

  publishAllDraftsApi(false);
};

  const openDraftView = (draft) => {
    setFormData({ ...initialFormState, ...draft });
    setModalMode("view_draft");
    setActiveTab("basic");
    setShowDraftModal(false);
    setShowModal(true);
  };


  /* ---------------------- Render Helpers ---------------------- */
  const isReadOnly =
    modalMode === "view" ||
    modalMode === "view_draft";
  // Reusable Input Field Component


  // ESLint Fix: Added prop types for FormInput
  FormInput.propTypes = {
    label: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    type: PropTypes.string,
    options: PropTypes.array,
  };

  if (access.loading) {
    return (
      <Layout>
        <div style={{ padding: 40, color: "#64748b" }}>Checking your access…</div>
      </Layout>
    );
  }

  if (!access.canView && !access.canCreate) {
    return (
      <Layout>
        <div style={{ padding: 40 }}>
          <h4 style={{ color: "#1e293b" }}>No access</h4>
          <p style={{ color: "#64748b" }}>
            Your role doesn't include the Business Partner module. Ask an administrator to add it to your role.
          </p>
        </div>
      </Layout>
    );
  }

  const accessCtx = {
    fieldState: (field) => access.fieldState(field, modalMode)
  };

  return (
    <AccessContext.Provider value={accessCtx}>
    <Layout>
      <style>{globalCSS}</style>

      {/* Header */}
      <div style={styles.pageTitleBar}>
        <div>
          <div style={styles.pageTitle}>Business Partner</div>
          <div style={styles.pageSubtitle}>Search, create, and manage vendors/customers</div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {access.canCreate && (
            <button onClick={() => setShowUploadModal(true)} className="btn btn-outline-dark" style={styles.outlineBtn}>
              ☁️ Create Business Partner By Upload
            </button>
          )}
          {access.canCreate && (
            <button className="btn btn-dark" style={styles.primaryBtn} onClick={openCreateModal}>
              + Add Business Partner
            </button>
          )}
        </div>
      </div>

    {/* Search Card */}
<div style={styles.filterCard}>

  {/* First Row */}
  <div className="row g-3 mb-3">
    <div className="col-md-4">
      <label style={styles.compactLabel}>Business Partner Type</label>

      <Select
        styles={customSelectStyles}
        options={[
          { value: "Supplier", label: "Supplier" },     
          { value: "Customer", label: "Customer" }
        ]}
        value={type ? { value: type, label: type } : null}
        onChange={(v) => setType(v ? v.value : "")}
        isClearable
      />
    </div>
  </div>

{/* Second Row */}
<div className="row g-3">

  {/* Business Type */}
  <div className="col-md-4">
    <label style={styles.compactLabel}>Business Type</label>

    <Select
      styles={customSelectStyles}
      options={[
        { value: "Proprietorship", label: "Proprietorship" },
        { value: "Partnership", label: "Partnership" },
        { value: "Private Limited", label: "Private Limited" },
        { value: "Public Limited", label: "Public Limited" }
      ]}
      value={by ? { value: by, label: by } : null}
      onChange={(v) => setBy(v ? v.value : "")}
      isClearable
    />
  </div>

  {/* Segment */}
  <div className="col-md-4">
    <label style={styles.compactLabel}>Segment</label>

    <Select
      styles={customSelectStyles}
      options={segments.map((item) => ({
        value: item.name,
        label: item.name
      }))}
      value={
        segment
          ? {
              value: segment,
              label: segment
            }
          : null
      }
      onChange={(v) =>
        setSegment(v ? v.value : "")
      }
      isClearable
    />
  </div>

  {/* Business Partner */}
  <div className="col-md-4">
    <label style={styles.compactLabel}>Business Partner</label>

    <input
      className="form-control"
      style={styles.compactInput}
      placeholder="Search Partner..."
      value={firstName}
      onChange={(e) => setFirstName(e.target.value)}
    />
  </div>

</div>

</div>
     

      {/* Table Card */}
      {/* Table Container */}
      <div style={styles.tableContainer}>
        {loading && (
          <div style={{ textAlign: "center", padding: 15 }}>
            Loading...
          </div>
        )}

        <table className="table" style={styles.erpTable}>
          <thead>
            <tr>
              <th style={styles.erpTh}>Business Partner</th>
              <th style={styles.erpTh}>Segment</th>
              <th style={styles.erpTh}>Business Partner Type</th>
              <th style={styles.erpTh}>GST</th>

              <th style={styles.erpTh}></th>
            </tr>
          </thead>

          <tbody>
            {access.canView && vendors?.length ? (
              paginatedData.map((v) => (
                <tr key={v.id}>
                  <td style={styles.erpTd}>
                    {access.fieldState("business_partner_name", "view").masked ? <MaskedValue height={22} /> : (v.business_partner_name || "").toUpperCase()}
                  </td>

                  <td style={styles.erpTd}>
                    {access.fieldState("category_master", "view").masked ? <MaskedValue height={22} /> : v.segment}
                  </td>

                  <td style={styles.erpTd}>
                    {access.fieldState("partner_type", "view").masked ? <MaskedValue height={22} /> : v.partner_type}
                  </td>

                  <td style={styles.erpTd}>
                    {access.fieldState("gstin", "view").masked ? <MaskedValue height={22} /> : v.gstin}
                  </td>

                  <td style={styles.erpTd}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        className="btn btn-outline-dark"
                        style={{ height: "28px", fontSize: "11px", padding: "2px 8px" }}
                        onClick={() => handleViewVendor(v)}
                      >
                        View
                      </button>

                      {access.canEdit && (
                      <button
                        title="Delete"
                        style={{
                          height: "28px",
                          width: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                          background: "#fee2e2",
                          border: "1px solid #fecaca",
                          borderRadius: "4px",
                          cursor: "pointer"
                        }}
                        onClick={() => handleDeleteVendor(v.id)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="text-center text-muted p-3"
                >
                  {access.canView
                    ? "No Records Found"
                    : "You can create business partners, but your role can't view existing ones."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
          marginTop: "15px",
          width: "100%"
        }}
      >
        <button
          className="btn btn-light"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          {"<"}
        </button>

        <span>
          Page {page} of {totalPages}
        </span>

        <button
          className="btn btn-light"
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
        >
          {">"}
        </button>
      </div>

      {/* ===================== UNIFIED PARTNER MODAL ===================== */}
      {showModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div style={{ fontWeight: 600, fontSize: '18px' }}>
                {modalMode === "create" && "Create Business Partner"}
                {modalMode === "view" && `Business Partner - ${formData.partner_code}`}
                {modalMode === "edit" && `Edit Business Partner - ${formData.partner_code}`}
                {modalMode === "view_draft" && `Preview Draft - ${formData.partner_code}`}
                {modalMode === "edit_draft" && `Edit Draft - ${formData.partner_code}`}
              </div>
              <button style={styles.iconBtn} onClick={() => {
                setShowModal(false);
                if (modalMode === "DRAFT") setShowDraftModal(true);
              }}>✕</button>
            </div>

            <div style={styles.modalBody}>
              {/* HEADER DROPDOWNS: Partner Type, Business Type, Group */}
              <div className="card p-3 mb-3 bg-light border-0">

                <div className="row g-3">

<Guard field="partner_type" label="Business Partner Type" col="col-md-4">
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>
                      Business Partner Type
                    </label>

                    <select
                      className="form-select"
                      style={styles.compactInput}
                      value={formData.partner_type || ""}
                      onChange={e =>
                        handleInputChange(
                          "partner_type",
                          e.target.value
                        )
                      }
                      disabled={isReadOnly}
                    >
                      <option value="">
                        Select Business Partner Type
                      </option>

                      <option value="Supplier">
                        Supplier
                      </option>

                      <option value="Customer">
                        Customer
                      </option>
                    </select>
                  </div>
</Guard>

<Guard field="business_type" label="Business Type" col="col-md-4">
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>
                      Business Type
                    </label>

                    <select
                      className="form-select"
                      style={styles.compactInput}
                      value={formData.business_type || ""}
                      onChange={e =>
                        handleInputChange(
                          "business_type",
                          e.target.value
                        )
                      }
                      disabled={isReadOnly}
                    >
                      <option value="">
                        Select Business Type
                      </option>

                      <option value="Proprietorship">
                        Proprietorship
                      </option>

                      <option value="Partnership">
                        Partnership
                      </option>

                      <option value="Private Limited">
                        Private Limited
                      </option>

                      <option value="Public Limited">
                        Public Limited
                      </option>

                      <option value="LLP">
                        LLP
                      </option>

                      <option value="Trust">
                        Trust
                      </option>

                      <option value="Society">
                        Society
                      </option>
                    </select>
                  </div>
</Guard>

<Guard field="business_partner_group" label="Business Partner Group" col="col-md-4">
                  <div className="col-md-4">
                    <label style={styles.compactLabel}>
                      Business Partner Group
                    </label>

                    <select
                      className="form-select"
                      style={styles.compactInput}
                      value={formData.business_partner_group || ""}
                      onChange={e =>
                        handleInputChange(
                          "business_partner_group",
                          e.target.value
                        )
                      }
                      disabled={isReadOnly}
                    >
                      <option value="">
                        Select Group
                      </option>

                      <option value="High">
                        High
                      </option>

                      <option value="Low">
                        Low
                      </option>
                    </select>
                  </div>
</Guard>

                </div>

              </div>

              {/* Modern Tab Navigation */}
              <div style={styles.tabsContainer}>
                {[
                  { id: "basic", label: "Basic Info" },
                  { id: "address", label: "Address" },
                  { id: "statutory", label: "Statutory" },
                  { id: "bank", label: "Bank Details" },
                  { id: "docs", label: "Docs" },
                  ...(modalMode !== "create" ? [{ id: "changeLog", label: "Change Log" }] : []),
                ].map((tab) => (
                  <button
                    key={tab.id}
                    style={activeTab === tab.id ? styles.activeTab : styles.inactiveTab}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* TAB CONTENT: BASIC INFO */}
              {activeTab === "basic" && (
                <div className="row g-3">

                  <FormInput
                    label="Business Partner Name"
                    field="business_partner_name"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Legal Name"
                    field="legal_name"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

<Guard field="category_master" label="Segment" col="col-md-4">
                 <div className="col-md-4">

  <label style={styles.compactLabel}>
    Segment
  </label>

  <Select
    styles={customSelectStyles}
    options={segments.map((item) => ({
      value: item.name,
      label: item.name
    }))}

    value={
      formData.segment
        ? {
            value: formData.segment,
            label: formData.segment
          }
        : null
    }

    onChange={(selected) =>
      handleInputChange(
        "segment",
        selected ? selected.value : ""
      )
    }

    isDisabled={isReadOnly}
    isClearable
  />

</div>
</Guard>





                  

<Guard field="business_partner_responsible" label="Business Partner Responsible" col="col-md-4">
                  <div className="col-md-4">

                    <label style={styles.compactLabel}>
                      Business Partner Responsible 
                    </label>

                    <Select
                      styles={customSelectStyles}
                      options={businessPartnerResponsibles.map((item) => ({
                        value: item.name,
                        label: item.name
                      }))}

                      value={
                        formData.business_partner_responsible
                          ? {
                            value: formData.business_partner_responsible,
                            label: formData.business_partner_responsible
                          }
                          : null
                      }

                      onChange={(selected) => {
                        handleInputChange(
                          "business_partner_responsible",
                          selected ? selected.value : ""
                        );

                        // Autofill Phone No + Email from the selected master row
                        const match = selected
                          ? businessPartnerResponsibles.find(
                              (item) => item.name === selected.value
                            )
                          : null;

                        handleInputChange(
                          "partner_responsible_phone",
                          match?.phone_number || ""
                        );
                        handleInputChange(
                          "partner_responsible_email",
                          match?.email || ""
                        );
                      }}

                      isDisabled={isReadOnly}
                      isClearable
                    />

                  </div>
</Guard>

                  <FormInput
                    label="Phone No"
                    field="partner_responsible_phone"
                    type="tel"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Email"
                    field="partner_responsible_email"
                    type="email"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

<div className="col-md-4">

  <button
    type="button"
    className="btn btn-outline-dark mt-4"
    style={{
      height: "28px",
      fontSize: "12px",
      padding: "0 10px"
    }}
    onClick={() => {

      const phones = safeParseArray(formData.contact_details);
      const names = safeParseArray(formData.contact_person_name);
      const designations = safeParseArray(formData.contact_person_designation);
      const emails = safeParseArray(formData.email);

      const maxLen = Math.max(
        phones.length,
        names.length,
        designations.length,
        emails.length
      );

      const rebuilt = Array.from({ length: maxLen }, (_, i) => ({
        phone_number: phones[i] || "",
        contact_person_name: names[i] || "",
        contact_person_designation: designations[i] || "",
        email: emails[i] || ""
      }));

      setPartnerContacts(rebuilt);
      setShowPartnerModal(true);
    }}
  >
    Manage Partner 
  </button>

</div>

<Guard field="terms_and_conditions" label="Terms & Conditions" col="col-md-12">
                  <div className="col-md-12">

                    <label style={styles.compactLabel}>
                      Terms & Conditions
                    </label>

                    <div
                      style={{
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        padding: "0 12px",
                        height: "28px",
                        background: "#fff",
                        fontSize: "13px",
                        fontFamily: "inherit",
                        color: "#6c757d",
                        display: "flex",
                        alignItems: "center"
                      }}
                    >
                      {
                        formData.terms_and_conditions
                          ? (
                            formData.terms_and_conditions.length > 50
                              ? formData.terms_and_conditions.substring(0, 50) + "..."
                              : formData.terms_and_conditions
                          )
                          : "No Terms & Conditions Added"
                      }
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      style={{
                        height: "28px",
                        fontSize: "12px",
                        padding: "0 10px",
                        marginTop: "4px"
                      }}
                      onClick={() => {
                        setTermsDraft(
                          formData.terms_and_conditions || ""
                        );
                        setShowTermsModal(true);
                      }}
                    >
                      {formData.terms_and_conditions
                        ? "View / Edit Terms"
                        : "Add Terms & Conditions"}
                    </button>

                  </div>
</Guard>



                  <div className="col-12">
                    <hr className="my-2" />
                  </div>


                </div>
              )}


              {/* TAB CONTENT: ADDRESS */}
              {activeTab === "address" && (
                <div className="row g-3">

                  {formData.partner_type === "Supplier" ? (

                    /* SUPPLIER — single Address block, backed by its own
                       dedicated address_* columns (separate from shipping_/billing_). */
<Guard field="address" label="Address" col="col-md-6">
                    <div className="col-md-6">

                      <label style={styles.compactLabel}>
                        Address
                      </label>

                      <div className="mb-2">
                        <label style={styles.compactLabel}>Address Line 1</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={formData.address_line1 || ""}
                          onChange={(e) =>
                            handleInputChange("address_line1", e.target.value)
                          }
                          disabled={isReadOnly}
                        />
                      </div>

                      <div className="mb-2">
                        <label style={styles.compactLabel}>Address Line 2</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={formData.address_line2 || ""}
                          onChange={(e) =>
                            handleInputChange("address_line2", e.target.value)
                          }
                          disabled={isReadOnly}
                        />
                      </div>

                      <div className="mb-2">
                        <label style={styles.compactLabel}>City</label>
                        <Select
                          styles={customSelectStyles}
                          options={cities.map(item => ({
                            value: item.name,
                            label: item.name
                          }))}
                          value={
                            formData.address_city
                              ? {
                                value: formData.address_city,
                                label: formData.address_city
                              }
                              : null
                          }
                          onChange={(selected) =>
                            handleInputChange(
                              "address_city",
                              selected ? selected.value : ""
                            )
                          }
                          isDisabled={isReadOnly}
                          isClearable
                        />
                      </div>

                      <div className="mb-2">
                        <label style={styles.compactLabel}>ZIP</label>
                        <input
                          type="text"
                          className="form-control"
                          style={styles.compactInput}
                          value={formData.address_zip || ""}
                          onChange={(e) =>
                            handleInputChange("address_zip", e.target.value)
                          }
                          disabled={isReadOnly}
                        />
                      </div>

                      <div className="mb-2">
                        <label style={styles.compactLabel}>State</label>
                        <Select
                          styles={customSelectStyles}
                          options={states.map(item => ({
                            value: item.name,
                            label: item.name
                          }))}
                          value={
                            formData.address_state
                              ? {
                                value: formData.address_state,
                                label: formData.address_state
                              }
                              : null
                          }
                          onChange={(selected) =>
                            handleInputChange(
                              "address_state",
                              selected ? selected.value : ""
                            )
                          }
                          isDisabled={isReadOnly}
                          isClearable
                        />
                      </div>

                      <div className="mb-2">
                        <label style={styles.compactLabel}>Country</label>
                        <Select
                          styles={customSelectStyles}
                          options={countries.map(item => ({
                            value: item.name,
                            label: item.name
                          }))}
                          value={
                            formData.address_country
                              ? {
                                value: formData.address_country,
                                label: formData.address_country
                              }
                              : null
                          }
                          onChange={(selected) =>
                            handleInputChange(
                              "address_country",
                              selected ? selected.value : ""
                            )
                          }
                          isDisabled={isReadOnly}
                          isClearable
                        />
                      </div>

                    </div>
</Guard>

                  ) : (

                    <>
                      {/* Ship To Address */}
<Guard field="shipping_address" label="Ship To Address" col="col-md-6">
                      <div className="col-md-6">

                        <label style={styles.compactLabel}>
                          Ship To Address
                        </label>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>Address Line 1</label>
                          <input
                            type="text"
                            className="form-control"
                            style={styles.compactInput}
                            value={formData.shipping_line1 || ""}
                            onChange={(e) =>
                              handleInputChange("shipping_line1", e.target.value)
                            }
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>Address Line 2</label>
                          <input
                            type="text"
                            className="form-control"
                            style={styles.compactInput}
                            value={formData.shipping_line2 || ""}
                            onChange={(e) =>
                              handleInputChange("shipping_line2", e.target.value)
                            }
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>City</label>
                          <Select
                            styles={customSelectStyles}
                            options={cities.map(item => ({
                              value: item.name,
                              label: item.name
                            }))}
                            value={
                              formData.shipping_city
                                ? {
                                  value: formData.shipping_city,
                                  label: formData.shipping_city
                                }
                                : null
                            }
                            onChange={(selected) =>
                              handleInputChange(
                                "shipping_city",
                                selected ? selected.value : ""
                              )
                            }
                            isDisabled={isReadOnly}
                            isClearable
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>ZIP</label>
                          <input
                            type="text"
                            className="form-control"
                            style={styles.compactInput}
                            value={formData.shipping_zip || ""}
                            onChange={(e) =>
                              handleInputChange("shipping_zip", e.target.value)
                            }
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>State</label>
                          <Select
                            styles={customSelectStyles}
                            options={states.map(item => ({
                              value: item.name,
                              label: item.name
                            }))}
                            value={
                              formData.shipping_state
                                ? {
                                  value: formData.shipping_state,
                                  label: formData.shipping_state
                                }
                                : null
                            }
                            onChange={(selected) =>
                              handleInputChange(
                                "shipping_state",
                                selected ? selected.value : ""
                              )
                            }
                            isDisabled={isReadOnly}
                            isClearable
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>Country</label>
                          <Select
                            styles={customSelectStyles}
                            options={countries.map(item => ({
                              value: item.name,
                              label: item.name
                            }))}
                            value={
                              formData.shipping_country
                                ? {
                                  value: formData.shipping_country,
                                  label: formData.shipping_country
                                }
                                : null
                            }
                            onChange={(selected) =>
                              handleInputChange(
                                "shipping_country",
                                selected ? selected.value : ""
                              )
                            }
                            isDisabled={isReadOnly}
                            isClearable
                          />
                        </div>

                      </div>
</Guard>

                      {/* Bill To Address */}
<Guard field="billing_address" label="Bill To Address" col="col-md-6">
                      <div className="col-md-6">

                        <label style={styles.compactLabel}>
                          Bill To Address
                        </label>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>Address Line 1</label>
                          <input
                            type="text"
                            className="form-control"
                            style={styles.compactInput}
                            value={formData.billing_line1 || ""}
                            onChange={(e) =>
                              handleInputChange("billing_line1", e.target.value)
                            }
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>Address Line 2</label>
                          <input
                            type="text"
                            className="form-control"
                            style={styles.compactInput}
                            value={formData.billing_line2 || ""}
                            onChange={(e) =>
                              handleInputChange("billing_line2", e.target.value)
                            }
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>City</label>
                          <Select
                            styles={customSelectStyles}
                            options={cities.map(item => ({
                              value: item.name,
                              label: item.name
                            }))}
                            value={
                              formData.billing_city
                                ? {
                                    value: formData.billing_city,
                                    label: formData.billing_city
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              handleInputChange(
                                "billing_city",
                                selected ? selected.value : ""
                              )
                            }
                            isDisabled={isReadOnly}
                            isClearable
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>ZIP</label>
                          <input
                            type="text"
                            className="form-control"
                            style={styles.compactInput}
                            value={formData.billing_zip || ""}
                            onChange={(e) =>
                              handleInputChange("billing_zip", e.target.value)
                            }
                            disabled={isReadOnly}
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>State</label>
                          <Select
                            styles={customSelectStyles}
                            options={states.map(item => ({
                              value: item.name,
                              label: item.name
                            }))}
                            value={
                              formData.billing_state
                                ? {
                                    value: formData.billing_state,
                                    label: formData.billing_state
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              handleInputChange(
                                "billing_state",
                                selected ? selected.value : ""
                              )
                            }
                            isDisabled={isReadOnly}
                            isClearable
                          />
                        </div>

                        <div className="mb-2">
                          <label style={styles.compactLabel}>Country</label>
                          <Select
                            styles={customSelectStyles}
                            options={countries.map(item => ({
                              value: item.name,
                              label: item.name
                            }))}
                            value={
                              formData.billing_country
                                ? {
                                  value: formData.billing_country,
                                  label: formData.billing_country
                                }
                                : null
                            }
                            onChange={(selected) =>
                              handleInputChange(
                                "billing_country",
                                selected ? selected.value : ""
                              )
                            }
                            isDisabled={isReadOnly}
                            isClearable
                          />
                        </div>

                      </div>
</Guard>
                    </>
                  )}

                </div>
              )}

              {/* TAB CONTENT: STATUTORY */}
              {activeTab === "statutory" && (
                <div className="row g-3">

                  <FormInput
                    label="PAN"
                    field="pan"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="PAN Status"
                    field="pan_status"
                    options={["Active", "Inactive", "Cancelled", "Under Verification"]}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Date of Corporation"
                    field="date_of_corporation"
                    type="date"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />


                  <FormInput
                    label="PAN Holder Category"
                    field="pan_holder_category"
                    options={[
                      "Individual",
                      "Company",
                      "Partnership Firm",
                      "LLP",
                      "Trust",
                      "Society",
                      "Government"
                    ]}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="GSTIN"
                    field="gstin"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="CIN"
                    field="cin"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Udyam / MSME Registration"
                    field="udyam_registration"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Shop / Establishment License"
                    field="shop_establishment_license"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="TAN"
                    field="tds_tan"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="ESI / PF No."
                    field="esi_pf"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Professional Tax"
                    field="professional_tax"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Import/Export Code (IEC)"
                    field="import_export_code"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Validation with GST/Bank"
                    field="pan_gstin_or_bank_validation"
                    options={["Yes", "No"]}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />


                </div>
              )}

              {/* TAB CONTENT: BANKING */}
              {activeTab === "bank" && (
                <div className="row g-3">

                  <FormInput
                    label="Bank Name"
                    field="bank_name"
                    options={[
                      "State Bank of India",
                      "HDFC Bank",
                      "ICICI Bank",
                      "Axis Bank",
                      "Punjab National Bank",
                      "Bank of Baroda",
                      "Kotak Mahindra Bank",
                      "Other"
                    ]}
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Branch"
                    field="branch"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="Account Number"
                    field="account_number"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />

                  <FormInput
                    label="IFSC Code"
                    field="ifsc_code"
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleFileChange={handleFileChange}
                    isReadOnly={isReadOnly}
                  />



                </div>
              )}

              {/* TAB CONTENT: TAX & COMPLIANCE */}

              {activeTab === "docs" && (
                <div style={{ background: "#fff", borderRadius: 8, padding: 16, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>

                  {/* Upload drop zone — same look as Sales Order, same underlying input/handler as before */}
                  <div style={{ marginBottom: 16 }}>
                    <label
                      htmlFor="vendorDocFileInput"
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        justifyContent: "center", border: "2px dashed #cbd5e1", borderRadius: 8,
                        padding: "18px 12px", cursor: "pointer",
                        background: "#f8fafc"
                      }}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <span style={{ fontSize: 22 }}>📁</span>
                      <span style={{ fontSize: 13, color: "#475569", fontWeight: 500, marginTop: 4 }}>
                        Click to attach documents
                      </span>
                      <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                        PDF, Word, Excel, Images, etc. (max 50 KB per file)
                      </span>
                      <input
                        id="vendorDocFileInput"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleDocumentUpload}
                      />
                    </label>
                  </div>

                  {/* Selected files list */}
                  {documents.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8", fontSize: 13 }}>
                      No documents attached yet.
                    </div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={styles.erpTh}>#</th>
                          <th style={styles.erpTh}>File Name</th>
                          <th style={styles.erpTh}>Size</th>
                          <th style={styles.erpTh}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc, idx) => (
                          <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                            <td style={styles.erpTd}>{idx + 1}</td>
                            <td style={styles.erpTd}>
                              <span style={{ color: "#1e293b", fontWeight: 500 }}>{doc.name}</span>
                            </td>
                            <td style={styles.erpTd}>
                              {doc.size > 1024
                                ? `${(doc.size / 1024).toFixed(1)} KB`
                                : `${doc.size} B`}
                            </td>
                            <td style={styles.erpTd}>
                              <button
                                type="button"
                                onClick={() => removeSelectedDocument(idx)}
                                style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14 }}
                                title="Remove"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {activeTab === "changeLog" && (

                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    padding: 12,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    border: "1px solid #e2e8f0"
                  }}
                >

                  <div className="table-responsive">

                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "13px"
                      }}
                    >

                      <thead>

                        <tr style={{ background: "#f8fafc" }}>

                          <th style={styles.logTh}>Date & Time</th>
                          <th style={styles.logTh}>User</th>
                          <th style={styles.logTh}>Action</th>
                          <th style={styles.logTh}>Status</th>
                          <th style={styles.logTh}>Field</th>
                          <th style={styles.logTh}>Old Value</th>
                          <th style={styles.logTh}>New Value</th>
                          <th style={styles.logTh}>Comment</th>

                        </tr>

                      </thead>

                      <tbody>

                        {partnerLogs.length > 0 ? (

                          partnerLogs.map((log) => (

                            <tr key={log.id}>

                              <td style={styles.logTd}>
                                {log.created_at
                                  ? new Date(log.created_at).toLocaleString()
                                  : "-"}
                              </td>

                              <td style={styles.logTd}>
                                {log.created_by || "-"}
                              </td>

                              <td style={styles.logTd}>
                                {log.action_type || "-"}
                              </td>

                              <td style={styles.logTd}>
                                {log.doc_status || "-"}
                              </td>

                              <td style={styles.logTd}>
                                {log.field_name || "-"}
                              </td>

                              <td style={styles.logTd}>
                                {log.old_value || "-"}
                              </td>

                              <td
                                style={{
                                  ...styles.logTd,
                                  fontWeight: 700,
                                  color: "#0f172a"
                                }}
                              >
                                {log.new_value || "-"}
                              </td>

                              <td style={styles.logTd}>
                                {log.comment || "-"}
                              </td>

                            </tr>

                          ))

                        ) : (

                          <tr>

                            <td
                              colSpan="8"
                              style={{
                                padding: 20,
                                textAlign: "center",
                                color: "#64748b"
                              }}
                            >
                              No change logs available
                            </td>

                          </tr>

                        )}

                      </tbody>

                    </table>

                  </div>

                </div>

              )}
            </div>



            {/* MODAL FOOTER */}
            <div style={styles.modalFooter}>

              <button
                className="btn btn-light"
                onClick={() => {
                  setShowModal(false);
                  if (
                    modalMode === "view_draft" ||
                    modalMode === "edit_draft"
                  ) setShowDraftModal(true);
                }}
              >
                Close
              </button>

              {modalMode === "view" && access.canEdit && (
                <button
                  className="btn btn-dark"
                  onClick={() => setModalMode("edit")}
                >
                  Edit
                </button>
              )}

              {modalMode === "view_draft" && access.canCreate && (
                <>
                  <button
                    className="btn btn-dark"
                    onClick={() => setModalMode("edit_draft")}
                  >
                    Edit Draft
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={() => {
                      setShowModal(false);
                      handlePublishDraft(formData.partner_code);
                    }}
                  >
                    Approve & Save Draft
                  </button>
                </>
              )}

              {[
                "create",
                "edit",
                "edit_draft"
              ].includes(modalMode) && (

                  <button
                    className="btn btn-dark"
                    onClick={saveOrUpdateVendor}
                  >
                    {modalMode === "create"
                      ? "Save"
                      : "Save"}
                  </button>

                )}

            </div>
          </div>
        </div>
      )}

      {/* ===================== FLOATING DRAFTS BADGE ===================== */}
      <div style={{ position: 'fixed', bottom: 30, right: 30, zIndex: 1040 }}>
        {access.canCreate && draftsList.length > 0 && (
          <div onClick={() => setShowDraftModal(true)} style={{ background: '#1e293b', color: '#fff', padding: '12px 20px', borderRadius: 50, cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 'bold', fontSize: 15 }}>📂 Pending Drafts</span>
            <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>{draftsList.length}</span>
          </div>
        )}
      </div>

      {/* ===================== UPLOAD MODAL ===================== */}
      {showUploadModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ background: "white", width: "400px", borderRadius: 12, padding: 25, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h4 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>Upload Partners</h4>
              <button onClick={() => setShowUploadModal(false)} style={styles.iconBtn}>✖</button>
            </div>
            <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: 30, textAlign: "center", background: "#f8fafc" }}>
              <p style={{ margin: "0 0 15px 0", fontSize: 14, color: "#475569" }}>Upload your Business Partner Template</p>
              <input type="file" id="partnerExcelUpload" accept=".xlsx, .xls, .csv" style={{ display: "none" }} onChange={handleBulkExcelUpload} />
              <label htmlFor="partnerExcelUpload" style={{ background: "#3b82f6", color: "white", padding: "8px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block", marginBottom: 15 }}>Browse File</label>
              <div>
                <a
                  href="/assets/files/businesspatner.xlsx"
                  download
                  style={{
                    fontSize: 13,
                    color: "#2563eb",
                    textDecoration: "none",
                    fontWeight: 500
                  }}
                >
                  ⬇ Download Template
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== DRAFTS PREVIEW MODAL ===================== */}
      {showDraftModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, width: "80%", maxWidth: "900px", height: "auto", maxHeight: "85vh" }}>

            {/* Standardized Header */}
            <div style={styles.modalHeader}>
              <div style={{ fontWeight: 600, fontSize: '18px' }}>👀 Preview Uploaded Partners</div>
              <button onClick={() => setShowDraftModal(false)} style={styles.iconBtn}>✕</button>
            </div>

            {/* Standardized Body (Auto-height with scrolling) */}
            <div style={styles.modalBody}>
              {draftsList.length === 0 ? (
                <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>No Pending Partners found.</p>
              ) : (
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Business Partner Code</th>
                      <th>Legal Name</th>
                      <th>Type</th>
                      <th>PAN</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftsList.map((draft) => (
                      <tr key={draft.partner_code}>
                        <td><strong>{draft.partner_code}</strong></td>
                        <td>{(draft.business_partner_name || draft.legal_name || "").toUpperCase()}</td>
                        <td>{draft.partner_type || "-"}</td>
                        <td>{draft.pan || "-"}</td>
                        <td style={{ display: "flex", gap: "8px", alignItems: "center" }}>

                          <button onClick={() => openDraftView(draft)} className="btn btn-primary btn-sm me-2" style={{ minWidth: "70px" }}>
                            View
                          </button>

                          <button onClick={() => onClickPublishDraft(draft.partner_code)} disabled={publishingId === draft.partner_code || isPublishingAll} className="btn btn-success btn-sm" style={{ minWidth: "70px" }}>
                            {publishingId === draft.partner_code ? "Saving..." : "Save"}
                          </button>

                          <button
                            onClick={() => handleDeleteDraft(draft.partner_code)}
                            disabled={deletingId === draft.partner_code || isPublishingAll}
                            className="btn btn-outline-danger btn-sm"
                            title="Delete Draft"
                            style={{ minWidth: "36px" }}
                          >
                            {deletingId === draft.partner_code ? "..." : "🗑️"}
                          </button>

                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Standardized Footer */}
            {draftsList.length > 0 && (
              <div style={styles.modalFooter}>
                <button onClick={() => setShowDraftModal(false)} className="btn btn-light">Close</button>
                <button onClick={handlePublishAllDrafts} disabled={isPublishingAll} className="btn btn-success fw-bold px-4">
                  {isPublishingAll ? "Saving All..." : "Save All"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PER-ROW DRAFT REPLACE CONFIRM */}
{showDraftReplaceConfirm && (
  <div style={styles.modalBackdrop}>
    <div style={{ background: "#fff", width: "420px", borderRadius: 14, padding: "36px 32px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)", textAlign: "center", position: "relative" }}>
      <button
        onClick={() => { setShowDraftReplaceConfirm(false); setDraftReplaceTarget(null); }}
        style={{ position: "absolute", top: 16, right: 18, background: "transparent", border: "none", fontSize: 18, cursor: "pointer", color: "#94a3b8" }}
      >✖</button>
      <h4 style={{ margin: "0 0 12px", fontSize: "19px", fontWeight: 700, color: "#1e293b" }}>
        {draftReplaceType === "active" ? "Business Partner Already Exists" : "Business Partner Already Deleted"}
      </h4>
      <p style={{ fontSize: 14, color: "#64748b", marginBottom: 28, lineHeight: 1.5 }}>
        {draftReplaceType === "active"
          ? `This partner already exists. Do you want to overwrite its existing data with this upload?`
          : `This partner already exists but is deleted. Do you want to replace it with this new data?`}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          onClick={() => {
            const code = draftReplaceTarget;
            setShowDraftReplaceConfirm(false);
            handlePublishDraft(code, true);
          }}
          style={{ padding: "12px 16px", borderRadius: 10, border: "none", background: "#16a34a", color: "#fff", cursor: "pointer", fontWeight: 600, fontSize: 14, width: "100%" }}
        >
          Yes, Replace It
        </button>
        <button
          onClick={() => { setShowDraftReplaceConfirm(false); setDraftReplaceTarget(null); setDraftReplaceType("deleted"); }}
          style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "transparent", color: "#334155", cursor: "pointer", fontWeight: 500, fontSize: 14, width: "100%" }}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* SAVE-ALL BULK REPLACE CONFIRM */}
{showBulkReplaceConfirm && (() => {
  const conflictsInList = draftsList
    .map(d => d.partner_code)
    .filter(code => deletedConflictCodes.includes(code) || activeConflictCodes.includes(code));

  return (
    <div style={styles.modalBackdrop}>
      <div style={{ background: "#fff", width: "460px", borderRadius: 14, padding: "36px 32px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)", textAlign: "center", position: "relative" }}>
        <button
          onClick={() => setShowBulkReplaceConfirm(false)}
          style={{ position: "absolute", top: 16, right: 18, background: "transparent", border: "none", fontSize: 18, cursor: "pointer", color: "#94a3b8" }}
        >✖</button>
        <h4 style={{ margin: "0 0 12px", fontSize: "19px", fontWeight: 700, color: "#1e293b" }}>
          Some Partners Already Exist
        </h4>
        <p style={{ fontSize: 14, color: "#64748b", marginBottom: 28, lineHeight: 1.5 }}>
          <strong>{conflictsInList.length}</strong> partner(s) in this batch already exist.
          Continuing will overwrite the existing data for {conflictsInList.length > 1 ? "these partners" : "this partner"}. Do you want to proceed?
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            onClick={() => { setShowBulkReplaceConfirm(false); publishAllDraftsApi(true); }}
            style={{ padding: "12px 16px", borderRadius: 10, border: "none", background: "#16a34a", color: "#fff", cursor: "pointer", fontWeight: 600, fontSize: 14, width: "100%" }}
          >
            Yes, Replace All
          </button>
          <button
            onClick={() => setShowBulkReplaceConfirm(false)}
            style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "transparent", color: "#334155", cursor: "pointer", fontWeight: 500, fontSize: 14, width: "100%" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
})()}

      {showTermsModal && (

        <div style={styles.modalBackdrop}>

          <div
            style={{
              background: "#fff",
              width: "90%",
              maxWidth: "1200px",
              width: "95%",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              maxHeight: "90vh",
              overflow: "hidden"
            }}
          >

            {/* HEADER */}
            <div style={styles.modalHeader}>

              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 600
                }}
              >
                Terms & Conditions
              </div>

              <button
                style={styles.iconBtn}
                onClick={() =>
                  setShowTermsModal(false)
                }
              >
                ✕
              </button>

            </div>

            {/* BODY */}
            <div
              style={{
                padding: "20px",
                overflowY: "auto",
                flex: 1
              }}
            >
              <textarea
                className="form-control"
                value={termsDraft}
                disabled={isReadOnly}
                onChange={(e) => {

                  const text = e.target.value;

                  const words = text
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean);

                  if (words.length <= 1000) {
                    setTermsDraft(text);
                  }

                }}
                placeholder="Enter Terms & Conditions (Max 1000 words)"
                style={{
                  width: "100%",
                  minHeight: "450px",
                  fontSize: "14px",
                  resize: "vertical"
                }}
              />

              <div
                style={{
                  textAlign: "right",
                  marginTop: "8px",
                  fontSize: "12px",
                  color: "#64748b"
                }}
              >
                {
                  termsDraft
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .length
                } / 1000 words
              </div>
            </div>

            {/* FOOTER */}
            <div style={styles.modalFooter}>

              <button
                className="btn btn-light"
                onClick={() =>
                  setShowTermsModal(false)
                }
              >
                Close
              </button>

              {!isReadOnly && (
                <button
                  className="btn btn-dark"
                  onClick={() => {

                    handleInputChange(
                      "terms_and_conditions",
                      termsDraft
                    );

                    setShowTermsModal(false);

                  }}
                >
                  Save
                </button>
              )}

            </div>

          </div>

        </div>

      )}
{showPartnerModal && (
  <div style={styles.modalBackdrop}>
    <div
      style={{
        background: "#fff",
        width: "95%",
        maxWidth: "1200px",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        maxHeight: "80vh",
        overflow: "hidden",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)"
      }}
    >

      {/* HEADER */}
      <div style={styles.modalHeader}>
        <div style={{ fontSize: "16px", fontWeight: 600, color: "#0f172a" }}>
          Partner Contacts
        </div>
        <button style={styles.iconBtn} onClick={() => setShowPartnerModal(false)}>
          ✕
        </button>
      </div>

      {/* BODY */}
      <div style={{ padding: "16px", overflowY: "auto", flex: 1 }}>

        {/* + Add Contact button — matches other primary action buttons in the app */}
        <button
          type="button"
          style={{
            ...styles.primaryBtn,
            background: "#0f172a",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: "12px"
          }}
          onClick={addContact}
        >
          + Add 
        </button>

        {/* TABLE — uses styles.erpTable / erpTh / erpTd to match the rest of the app */}
        <div style={styles.tableContainer}>
          <table style={styles.erpTable}>
            <thead>
              <tr>
                <th style={styles.erpTh}>Phone Number</th>
                <th style={styles.erpTh}>Contact Person Name</th>
                <th style={styles.erpTh}>Designation</th>
                <th style={styles.erpTh}>Email</th>
                <th style={{ ...styles.erpTh, width: "80px", textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {partnerContacts.map((row, index) => (
                <tr key={index}>
                  <td style={styles.erpTd}>
                    <input
                      className="form-control"
                      style={styles.compactInput}
                      value={row.phone_number || ""}
                      onChange={(e) => updateContact(index, "phone_number", e.target.value)}
                    />
                  </td>
                  <td style={styles.erpTd}>
                    <input
                      className="form-control"
                      style={styles.compactInput}
                      value={row.contact_person_name || ""}
                      onChange={(e) => updateContact(index, "contact_person_name", e.target.value)}
                    />
                  </td>
                  <td style={styles.erpTd}>
                    <input
                      className="form-control"
                      style={styles.compactInput}
                      value={row.contact_person_designation || ""}
                      onChange={(e) => updateContact(index, "contact_person_designation", e.target.value)}
                    />
                  </td>
                  <td style={styles.erpTd}>
                    <input
                      className="form-control"
                      type="email"
                      style={styles.compactInput}
                      value={row.email || ""}
                      onChange={(e) => updateContact(index, "email", e.target.value)}
                    />
                  </td>
                  <td style={{ ...styles.erpTd, textAlign: "center" }}>
                    <button
                      style={{
                        background: "#ef4444",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "2px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer"
                      }}
                      onClick={() => removeContact(index)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

      {/* FOOTER — same as all other modals in the file */}
      <div style={styles.modalFooter}>
        <button
          style={{ ...styles.outlineBtn, borderRadius: "6px", fontSize: "13px", cursor: "pointer" }}
          onClick={() => setShowPartnerModal(false)}
        >
          Close
        </button>
        <button
          style={{
            ...styles.primaryBtn,
            background: "#0f172a",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            cursor: "pointer"
          }}
          onClick={() => {
            handleInputChange("contact_person_name", JSON.stringify(partnerContacts.map(x => x.contact_person_name)));
            handleInputChange("contact_person_designation", JSON.stringify(partnerContacts.map(x => x.contact_person_designation)));
            handleInputChange("contact_details", JSON.stringify(partnerContacts.map(x => x.phone_number)));
            handleInputChange("email", partnerContacts.map(x => x.email));
            setShowPartnerModal(false);
          }}
        >
          Save
        </button>
      </div>

    </div>
  </div>
)}
      <Toast toasts={toasts} remove={removeToast} />
    </Layout>
    </AccessContext.Provider>
  );
};

/* ---------------------- Global Styling ---------------------- */
const globalCSS = `
  body { background: #f8fafc !important; font-family: 'Inter', system-ui, sans-serif; }
  .btn { border-radius: 6px !important; }
  .form-control, .form-select { border-radius: 6px; border: 1px solid #cbd5e1; box-shadow: none !important; }
  .form-control:focus, .form-select:focus { border-color: #3b82f6; }
  .table thead th { background: #f1f5f9 !important; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0; }
  .table-hover tbody tr:hover { background: #f8fafc; }
`;

const styles = {
  pageTitleBar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: "#0f172a" },
  pageSubtitle: { fontSize: 14, color: "#64748b" },
  primaryBtn: { padding: "8px 20px", fontWeight: 600 },
  outlineBtn: { padding: "8px 16px", background: "white", color: "#1e293b", border: "1px solid #cbd5e1", fontWeight: 600 },

  card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 20 },
  loading: { textAlign: "center", padding: 15, color: "#64748b", fontWeight: 500 },

  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: 20, backdropFilter: "blur(2px)" },
  modal: { background: "#fff", borderRadius: 12, width: "95%", maxWidth: 1050, height: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" },
  modalHeader: { padding: "18px 22px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", borderTopLeftRadius: 12, borderTopRightRadius: 12 },

  modalBody: { padding: "10px 14px", overflowY: "auto", flex: 1, background: "#fff" },
  modalFooter: { padding: "14px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 12, background: "#f8fafc", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 },
  iconBtn: { background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" },

  tabsContainer: { display: "flex", gap: 4, marginBottom: 8, borderBottom: "1px solid #e2e8f0", paddingBottom: 4, overflowX: "auto", alignItems: "center" },
  activeTab: { background: "#0f172a", color: "white", border: "1px solid #0f172a", padding: "4px 10px", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer", height: "30px", display: "flex", alignItems: "center" },
  inactiveTab: { background: "transparent", color: "#64748b", border: "1px solid transparent", padding: "4px 10px", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", height: "30px", display: "flex", alignItems: "center" },

  toastContainer: { position: "fixed", right: 20, bottom: 20, display: "flex", flexDirection: "column", gap: 10, zIndex: 2000 },
  toast: { background: "#0f172a", color: "#fff", minWidth: 300, padding: "12px 40px 12px 16px", borderRadius: 8, position: "relative", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" },
  toastError: { background: "#ef4444" },
  toastClose: { position: "absolute", right: 12, top: 10, border: "none", background: "transparent", color: "#fff", fontSize: 18, cursor: "pointer" },

  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
  },

  erpTable: {
    width: "100%",
    borderCollapse: "collapse"
  },

  erpTh: {
    background: "#f8fafc",
    fontSize: 12,
    fontWeight: 600,
    padding: "10px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b"
  },

  erpTd: {
    fontSize: 13,
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155"
  },

  compactLabel: {
    fontSize: "11px",
    fontWeight: 600,
    marginBottom: "3px",
    color: "#334155"
  },

  compactInput: {
    height: "28px",
    fontSize: "12px",
    padding: "2px 8px",
    borderRadius: "4px"
  },

  filterCard: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 15,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: 15
  },
};


export default VendorManagement;